import { AlertTriangle, Check, GalleryHorizontal, ImageOff, Maximize2, Video } from 'lucide-react';
import { memo, useEffect, useRef, useState, type MouseEvent } from 'react';

import type { AssetResponse } from '../../api';
import {
  filmDurationLabel,
  formatDateDDMMYYYY,
  getAssetExpiryDate,
  getPublishBlockers,
} from '../../constants/taxonomy';
import { mediaKindOf, specOf } from '../../constants/assetTypes';
import { OlaPreview } from './OlaPreview';
import { sourceImageBadge } from './sourceImageBadge';

/**
 * One asset in the grid: filename, thumbnail, and the little metadata that
 * earns its vertical space.
 *
 * The card stays deliberately visual. Editable fields live in the details
 * drawer, not here — five inline inputs per card would roughly double the card
 * height and halve how many assets fit on screen, which defeats the point of a
 * visual grid for reviewing a 500-file batch. It would also make a click
 * ambiguous between "select this card" and "focus this field", and selection
 * is the gesture everything else here is built on.
 *
 * What does stay on the card is read-only and cheap: dimensions, the source image
 * code, and a warning when something blocks publishing. That warning is the one
 * piece of metadata worth the pixels, because a staged asset missing a
 * nameplate or a source image link is silently unpublishable — the backend skips
 * it — and users need to spot those by scanning rather than by opening each
 * card in turn.
 */

interface AssetCardProps {
  asset: AssetResponse;
  /** The original. Shown only when there is no `thumbnailUrl`. */
  url: string | undefined;
  /**
   * The grid thumbnail, when the asset has one: a small still for every kind,
   * a film's included, so the card never has to load the original.
   */
  thumbnailUrl?: string;
  /** True while URLs are in flight — distinguishes "not yet" from "never". */
  isUrlLoading: boolean;
  /** The backend resolved no URL for this asset: never coming, don't spin. */
  isMissing: boolean;
  isSelected: boolean;
  onSelect: (id: string, modifiers: { shift?: boolean; meta?: boolean }) => void;
  /** Open this asset in the full-screen detail overlay. */
  onOpen: (id: string) => void;
  /** Ask for a fresh round of signing after an image failed to load. */
  onUrlError: () => void;
  /**
   * Capability token letting the hover preview load this banner's files.
   * Only ever set for `asset_type === 'ola'`; absent means poster only.
   */
  olaToken?: string;
  /**
   * Dimmed: nothing can be done with this card here. Only ever for a card that
   * cannot be selected — a dimmed card that still selects reads as disabled to
   * the people it is not disabled for. Matching the picked brief row is
   * signalled by the basket button, not by dimming everything else.
   */
  isDimmed?: boolean;
  /**
   * When false the card shows no checkbox and a click opens it instead: a
   * selection this role could do nothing with is not offered, and a click that
   * does nothing reads as broken.
   */
  isSelectable?: boolean;
  /**
   * Add/remove this asset for the selected row. Absent on the Staged tab and
   * whenever no row is selected — there is nothing to add it *to*, and a button
   * that can only explain why it doesn't work is worse than no button.
   */
  onToggleBasket?: (id: string) => void;
  /** Already in the basket for the selected row. */
  inBasket?: boolean;
  /** When true the asset rights have lapsed (Published tab). */
  isExpired?: boolean;
  /** When true the asset rights expire within the next 90 days (Published tab). */
  isExpiringSoon?: boolean;
  /**
   * Prefix for the card's DOM ids. The grouped view renders a multi-nameplate
   * asset once per section, so the asset id alone would repeat in the DOM and
   * `aria-describedby` would resolve to whichever copy came first.
   */
  domScope?: string;
}

/**
 * One re-sign per failure. A signed URL that expired early recovers on the
 * first retry; an object that is genuinely gone 404s no matter how many times
 * it is re-signed, and without a cap that second case is an endless
 * error -> invalidate -> new URL -> error loop.
 *
 * The budget is per run of failures, not per session: it resets once an image
 * loads, so a card that recovered this morning can still recover this
 * afternoon when its URL expires again. A permanently dead object never
 * reaches the reset, so the loop stays capped.
 */
const MAX_URL_RETRIES = 1;

/**
 * A video tile that fetches nothing until it scrolls into view.
 *
 * `<video>` has no `loading="lazy"`, and `preload="metadata"` alone still
 * makes every film in a 500-asset page start fetching on render. The `src` is
 * withheld until the tile is visible, then kept, so the first frame still
 * shows as the thumbnail once it is.
 */
function LazyVideo({
  src,
  onError,
  onLoadedData,
}: {
  src: string;
  onError: () => void;
  onLoadedData: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isInView) return;
    // No observer (old browsers, jsdom): load straight away, as before.
    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [isInView]);

  return (
    <video
      ref={ref}
      src={isInView ? src : undefined}
      className="max-h-full max-w-full object-contain"
      preload="metadata"
      muted
      onError={onError}
      onLoadedData={onLoadedData}
    />
  );
}

/** Says a tile is a film, whether it shows the film or its still. */
function VideoChip({ duration }: { duration: string | null }) {
  return (
    <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-[2px]">
      <Video className="h-3 w-3" strokeWidth={2} />
      {duration || 'Video'}
    </div>
  );
}

function formatDimensions(asset: AssetResponse): string | null {
  if (asset.width === null || asset.height === null) {
    // A film's size is never read from the video; it declares its ratio.
    return asset.asset_type === 'filmic' ? asset.format_size : null;
  }
  return `${asset.width}×${asset.height}`;
}

/**
 * Memoised because the grid renders up to 500 of these and a selection click
 * changes one card's `isSelected`: every other prop is a primitive or a stable
 * callback, so the other 499 skip rendering.
 */
export const AssetCard = memo(function AssetCard({
  asset,
  url,
  thumbnailUrl,
  isUrlLoading,
  isMissing,
  isSelected,
  onSelect,
  onOpen,
  onUrlError,
  olaToken,
  isDimmed = false,
  isSelectable = true,
  onToggleBasket,
  inBasket = false,
  isExpired = false,
  isExpiringSoon = false,
  domScope = '',
}: AssetCardProps) {
  const dimensions = formatDimensions(asset);
  const duration =
    asset.duration_seconds !== null ? filmDurationLabel(asset.duration_seconds) : null;
  const frameCount = specOf(asset).hasFrames ? (asset.frames?.length ?? 0) : 0;
  const sourceBadge = sourceImageBadge(asset.source_image_codes);
  const expiryDate = getAssetExpiryDate(asset);

  // Names the filename element so the expand control can borrow it as its
  // description. The control's own label stays generic — "Open full view" —
  // because a per-card label would give two buttons on the same card names
  // that only differ by a verb, which is worse to hear read out than one
  // labelled button with a description.
  const filenameId = `asset-card-name-${domScope}${asset.id}`;

  // Both of these make an asset silently unpublishable — the backend skips it —
  // so they share one warning rather than competing for the same corner. The
  // reason is in the tooltip; the icon only has to say "this one needs a look".
  //
  // The same rule the Publish button reads, rather than a second copy that can
  // drift from it.
  const blockers = getPublishBlockers(asset);

  // The URL that failed, not a boolean: a freshly signed URL is a different
  // string, so the card un-breaks itself without an effect to reset it.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const retries = useRef(0);
  // What the card actually loads: the thumbnail, or the original until the
  // asset has one.
  const shownUrl = thumbnailUrl ?? url;
  const isBroken = shownUrl !== undefined && shownUrl === failedUrl;
  const isVideo = mediaKindOf(asset.asset_type) === 'video';

  const handleClick = (event: MouseEvent) => {
    // The second click of a double-click belongs to the open gesture. Without
    // this it is also handled as a selection click, and since a plain click on
    // the only selected asset deselects it, double-clicking a card would open
    // the overlay having just thrown away the selection that opened it — the
    // drawer flickering shut on the way.
    // For a card that opens on a single click, the second click would only
    // open it again.
    if (event.detail > 1) return;
    if (!isSelectable) {
      onOpen(asset.id);
      return;
    }
    onSelect(asset.id, { shift: event.shiftKey, meta: event.metaKey || event.ctrlKey });
  };

  const handleImageError = () => {
    if (shownUrl === undefined) return;
    setFailedUrl(shownUrl);
    if (retries.current >= MAX_URL_RETRIES) return;
    retries.current += 1;
    onUrlError();
  };

  // A successful load ends the run of failures, so the next expiry — hours
  // later, on a long-lived tab — gets its own retry rather than falling
  // straight through to the fallback.
  const handleImageLoad = () => {
    retries.current = 0;
  };

  return (
    // The wrapper exists so the expand control can be a sibling of the
    // selection button rather than sit inside it: a button nested in a button
    // is invalid, and browsers resolve it by dropping one of them.
    <div
      className={`group flex flex-col ${isDimmed ? 'opacity-40' : isExpired ? 'opacity-60' : ''}`}
    >
      {/* Positioning context for the expand control. It belongs to the card
          rather than to the whole tile, so the basket button below can't be
          covered by it. */}
      <div className="relative">
        {/* A button, not a div with a click handler: selection is a real control,
          so it should be tabbable and respond to Enter/Space without
          reimplementing either. `text-left` undoes the centring buttons get by
          default. */}
        <button
          type="button"
          onClick={handleClick}
          // Double-click opens, matching the file-manager conventions the rest of
          // the selection model already follows. The explicit control below is
          // what makes that discoverable — and is the only one of the two a
          // keyboard or screen-reader user can reach. Only where a click selects:
          // a card that cannot be selected already opens on the first click, and
          // opening it again would push a second history entry, so Back would
          // take two presses to close it.
          onDoubleClick={isSelectable ? () => onOpen(asset.id) : undefined}
          aria-pressed={isSelectable ? isSelected : undefined}
          className={`flex w-full flex-col overflow-hidden border bg-white text-left transition-all ${
            onToggleBasket && !isExpired ? 'rounded-t-lg' : 'rounded-lg'
          } ${
            isSelected
              ? 'border-[#0014cc] ring-2 ring-[#0014cc]/20'
              : inBasket
                ? 'border-[#0014cc]/40'
                : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div
            className={`flex items-center gap-2 border-b px-2.5 py-1.5 transition-colors ${
              isSelected ? 'border-[#0014cc]/20 bg-[#0014cc]/5' : 'border-slate-100 bg-slate-50'
            }`}
          >
            {/* Presentational: the card itself carries the pressed state and the
              keyboard affordance, so a real nested checkbox would be a second
              tab stop and a second thing to keep in sync for the same action.
              Absent, not greyed, where the card cannot be selected. */}
            {isSelectable && (
              <span
                aria-hidden="true"
                data-testid="card-checkbox"
                className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors ${
                  isSelected ? 'border-[#0014cc] bg-[#0014cc]' : 'border-slate-300 bg-white'
                }`}
              >
                {isSelected && (
                  <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 text-white" fill="none">
                    <path
                      d="M2.5 6.5l2.5 2.5 4.5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            )}
            {/* `truncate` needs a min-width-0 flex child or the long filename
              forces the card wider than its grid track. */}
            <span
              id={filenameId}
              className="min-w-0 flex-1 truncate text-[11.5px] font-medium text-slate-700"
              title={asset.filename_original}
            >
              {asset.filename_original}
            </span>
            {blockers.length > 0 && (
              <span title={`${blockers.join(' and ')} required before this asset can be published`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" strokeWidth={2} />
              </span>
            )}
          </div>

          {/* Fixed height with object-contain: creatives run from 300×250 to
            300×600, and letting each card size to its image would leave the grid
            ragged. Containing rather than cropping matters because the whole
            point of the thumbnail is judging the creative. */}
          <div className="relative flex h-44 items-center justify-center bg-slate-100/70 p-2">
            {isExpired && expiryDate ? (
              <span className="absolute left-2 top-2 z-10 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm">
                Expired {formatDateDDMMYYYY(expiryDate)}
              </span>
            ) : isExpiringSoon && expiryDate ? (
              <span className="absolute left-2 top-2 z-10 flex flex-col items-center rounded bg-amber-400 px-1.5 py-0.5 text-center text-[10px] font-semibold leading-tight text-slate-900 shadow-sm">
                <span>Expires soon</span>
                <span>{formatDateDDMMYYYY(expiryDate)}</span>
              </span>
            ) : inBasket ? (
              <span className="absolute left-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded bg-[#0014cc] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
                In basket
              </span>
            ) : null}
            {asset.asset_type === 'ola' ? (
              // Owns all three states itself — poster, skeleton, fallback —
              // because the live banner has to render over the tile whichever
              // of them is showing, and a poster that failed to sign is no
              // reason to refuse to play the ad.
              <OlaPreview
                asset={asset}
                posterUrl={isBroken ? undefined : shownUrl}
                token={olaToken}
                isUrlLoading={isUrlLoading && !isMissing && !isBroken}
                onPosterError={handleImageError}
                onPosterLoad={handleImageLoad}
              />
            ) : shownUrl && !isBroken ? (
              // A film without a thumbnail yet is the one case that still
              // loads the original, and only once it scrolls into view.
              isVideo && !thumbnailUrl ? (
                <div className="relative flex h-full w-full items-center justify-center">
                  <LazyVideo
                    src={shownUrl}
                    onError={handleImageError}
                    onLoadedData={handleImageLoad}
                  />
                  <VideoChip duration={duration} />
                </div>
              ) : (
                <>
                  <img
                    src={shownUrl}
                    alt={asset.filename_original}
                    loading="lazy"
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                    className="max-h-full max-w-full object-contain"
                  />
                  {isVideo && <VideoChip duration={duration} />}
                  {/* The card shows frame 1 only; this says there are more,
                      the way the HTML5 chip says a banner moves. */}
                  {frameCount > 0 && (
                    <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-[2px]">
                      <GalleryHorizontal className="h-3 w-3" strokeWidth={2} />
                      Carousel · {frameCount}
                    </div>
                  )}
                </>
              )
            ) : isUrlLoading && !isMissing && !isBroken ? (
              <div className="h-full w-full animate-pulse rounded bg-slate-200" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-slate-400">
                <ImageOff className="h-5 w-5" strokeWidth={1.5} />
                <span className="text-[11px]">Preview unavailable</span>
              </div>
            )}
          </div>

          {/* `pr-9` reserves the corner the expand control sits in, so a long
            nameplate runs out of room rather than under the button. */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 pr-9 text-[11px] text-slate-500">
            {/* The code leads, because "which hero is this?" is the question the
              grid is scanned for once a batch is more than a few assets. An
              asset cut from a source image shows that image's code as its Image
              ID, outlined rather than filled: same family, but the solid pill
              has to keep meaning "this is the hero". */}
            {asset.source_image_code ? (
              <span className="shrink-0 rounded bg-[#0014cc]/10 px-1.5 py-0.5 font-medium text-[#0014cc]">
                {asset.source_image_code}
              </span>
            ) : (
              sourceBadge && (
                <span
                  title={sourceBadge.title}
                  className="shrink-0 rounded border border-[#0014cc]/20 px-1.5 py-0.5 font-medium text-[#0014cc]/70"
                >
                  {sourceBadge.label}
                </span>
              )
            )}
            {duration && <span className="font-semibold text-slate-700">{duration}</span>}
            {dimensions && (
              <>
                {duration && <span className="text-slate-300">·</span>}
                <span>{dimensions}</span>
              </>
            )}
            {asset.nameplate && asset.nameplate.length > 0 && (
              <>
                {(dimensions || duration) && <span className="text-slate-300">·</span>}
                <span className="truncate text-slate-600">{asset.nameplate.join(', ')}</span>
              </>
            )}
          </div>
        </button>

        {/* Hidden until the card is hovered or this control is focused, so the
          grid stays a wall of creative rather than a wall of buttons — but it
          is always in the tab order, because double-click is a mouse-only
          gesture and opening an asset cannot be mouse-only.

          The label is generic and the filename arrives via `aria-describedby`:
          naming it "Open first.jpg" would give this button and the card itself
          near-identical accessible names. */}
        <button
          type="button"
          onClick={() => onOpen(asset.id)}
          aria-label="Open full view"
          aria-describedby={filenameId}
          className="absolute bottom-1.5 right-1.5 rounded-md border border-slate-200 bg-white/95 p-1 text-slate-500 opacity-0 shadow-sm transition-opacity hover:text-[#0014cc] focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Maximize2 className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>

      {/* Always visible, never hover-revealed: this is the primary action of
          the Published tab, and the one thing a client is here to do. Its own
          button rather than a click on the card, because the card already means
          "select", and a single gesture cannot mean both. */}
      {onToggleBasket && !isExpired && (
        <button
          type="button"
          onClick={() => onToggleBasket(asset.id)}
          className={`-mt-px rounded-b-lg border py-1.5 text-[12px] font-semibold transition-colors ${
            inBasket
              ? 'border-slate-200 bg-white text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600'
              : 'border-[#0014cc] bg-[#0014cc] text-white hover:bg-[#0010a3]'
          }`}
        >
          {inBasket ? 'Remove' : 'Add to basket'}
        </button>
      )}
    </div>
  );
});
